import type { CascaderOption } from '@/components/Form/CascaderField';
import { useOrganizationOptionsRecursive } from '@/features/tenant-manager/organization/_hooks/organization';
import { useUserOptionsForSelectField } from '@/features/tenant-manager/user/_hooks/user';
import { useMessage } from '@/providers';
import type { Dayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';
import type { UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type z from 'zod';
import type { ProjectFormSchema } from '../_model/project-form';

type ProjectFormValues = z.infer<typeof ProjectFormSchema>;

export const useSelectFiledFormData = ({
  inputFiscalYear,
  inputSnapshotId,
}: {
  inputFiscalYear: Dayjs | null | undefined;
  inputSnapshotId: string | null | undefined;
}) => {
  const organizationOptions = useOrganizationOptionsRecursive({
    params: {
      referenceFiscalYear: inputFiscalYear?.year().toString(),
      snapshotId: inputSnapshotId ?? undefined,
    },
    enabled: !!inputFiscalYear,
  });

  const userOptions = useUserOptionsForSelectField({
    params: { snapshotId: inputSnapshotId ?? undefined },
  });

  return {
    organizationOptions,
    userOptions,
  };
};

type ValidateSnapshotTargetResult = {
  result: 'success' | 'error';
};

const existOrganization = (
  allOrganization: CascaderOption[],
  targetOrganizationIds: string[],
): boolean => {
  // 同一階層構造で存在するか判定する
  const result = targetOrganizationIds.reduce<CascaderOption | undefined>(
    (acc, currentId, idx) => {
      if (idx === 0) {
        return allOrganization.find(
          (organization) => organization.value === currentId,
        );
      }
      return acc?.children?.find(
        (organization) => organization.value === currentId,
      );
    },
    undefined,
  );

  return !!result;
};

const existUsers = (allUserIds: string[], targetUserIds: string[]): boolean => {
  return targetUserIds.every((userId) =>
    allUserIds.some((id) => id === userId),
  );
};

export const useValidateSnapshotTarget = ({
  inputSnapshotId,
  inputFiscalYear,
}: {
  inputFiscalYear: Dayjs | null | undefined;
  inputSnapshotId: string | null | undefined;
}) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();

  const { userOptions, organizationOptions } = useSelectFiledFormData({
    inputFiscalYear,
    inputSnapshotId: inputSnapshotId ?? undefined,
  });

  const allOrganization = organizationOptions.data ?? [];

  const allUserIds = useMemo(() => {
    return userOptions.data?.map((user) => user.value);
  }, [userOptions.data]);

  const validSnapshotTarget = useCallback(
    ({
      setError,
      values,
    }: {
      setError: UseFormSetError<ProjectFormValues>;
      values: ProjectFormValues;
    }): ValidateSnapshotTargetResult => {
      let hasError = false;
      const {
        organizationIds,
        peopleInChargeIds,
        businessManagerIds,
        nestedOtherResponsibleOrganizationIds,
        nestedRelatedOrganizationIds,
        fiscalYearOfSnapshot,
      } = values.data;

      if (!fiscalYearOfSnapshot?.length) {
        return {
          result: 'success',
        };
      }

      // snapshotのデータが存在しない場合はエラーを返す
      if (!allUserIds?.length || !allOrganization?.length) {
        openMessage('error', t('unknown-error'));
        return { result: 'error' };
      }

      // 事業所管課室
      if (organizationIds.length) {
        if (!existOrganization(allOrganization, organizationIds)) {
          setError('data.organizationIds', {
            message: t('error-message-selected-un-exist-snapshot-value'),
          });
          hasError = true;
        }
      }

      // 事業担当者
      if (peopleInChargeIds?.length) {
        if (!existUsers(allUserIds, peopleInChargeIds)) {
          setError('data.peopleInChargeIds', {
            message: t('error-message-selected-un-exist-snapshot-value'),
          });
          hasError = true;
        }
      }

      // 作成責任者
      if (businessManagerIds.length) {
        if (!existUsers(allUserIds, businessManagerIds)) {
          setError('data.businessManagerIds', {
            message: t('error-message-selected-un-exist-snapshot-value'),
          });
          hasError = true;
        }
      }

      // その他担当組織
      if (nestedOtherResponsibleOrganizationIds?.length) {
        for (const [
          index,
          organizationIds,
        ] of nestedOtherResponsibleOrganizationIds.entries()) {
          if (!existOrganization(allOrganization, organizationIds)) {
            setError(`data.nestedOtherResponsibleOrganizationIds.${index}`, {
              message: t('error-message-selected-un-exist-snapshot-value'),
            });
            hasError = true;
          }
        }
      }

      // 関連組織
      if (nestedRelatedOrganizationIds?.length) {
        for (const [
          index,
          organizationIds,
        ] of nestedRelatedOrganizationIds.entries()) {
          if (!existOrganization(allOrganization, organizationIds)) {
            setError(`data.nestedRelatedOrganizationIds.${index}`, {
              message: t('error-message-selected-un-exist-snapshot-value'),
            });
            hasError = true;
          }
        }
      }

      // 独立行政法人
      if (values.sheetType === 'SS') {
        if (
          !existOrganization(
            allOrganization,
            values.data.independentAdministrativeAgencyIds,
          )
        ) {
          setError('data.independentAdministrativeAgencyIds', {
            message: t('error-message-selected-un-exist-snapshot-value'),
          });
          hasError = true;
        }
      }

      // 事業所管課室(追加)
      if (
        values.sheetType === 'KS' &&
        values.data.fundAdditionalAttributes?.fundSheetType === 'standard' &&
        values.data.fundAdditionalAttributes?.additionalJurisdictionalUsers
          ?.length
      ) {
        const additionalJurisdictionalUsers =
          values.data.fundAdditionalAttributes?.additionalJurisdictionalUsers;

        for (const [index, data] of additionalJurisdictionalUsers.entries()) {
          const { businessManagerIds, peopleInChargeIds, organizationIds } =
            data;

          if (organizationIds) {
            if (!existOrganization(allOrganization, organizationIds)) {
              setError(
                `data.fundAdditionalAttributes.additionalJurisdictionalUsers.${index}.organizationIds`,
                {
                  message: t('error-message-selected-un-exist-snapshot-value'),
                },
              );
              hasError = true;
            }
          }
          if (peopleInChargeIds) {
            if (!existUsers(allUserIds, peopleInChargeIds)) {
              setError(
                `data.fundAdditionalAttributes.additionalJurisdictionalUsers.${index}.peopleInChargeIds`,
                {
                  message: t('error-message-selected-un-exist-snapshot-value'),
                },
              );
              hasError = true;
            }
          }
          if (businessManagerIds) {
            if (!existUsers(allUserIds, businessManagerIds)) {
              setError(
                `data.fundAdditionalAttributes.additionalJurisdictionalUsers.${index}.businessManagerIds`,
                {
                  message: t('error-message-selected-un-exist-snapshot-value'),
                },
              );
              hasError = true;
            }
          }
        }
      }

      return {
        result: hasError ? 'error' : 'success',
      };
    },
    [openMessage, t, allUserIds, allOrganization],
  );

  return { validSnapshotTarget };
};
